import { motion } from "motion/react";
import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { TextScramble } from "../components/TextScramble";
import { ArrowUpRight } from "lucide-react";
import { MagneticButton } from "../components/MagneticButton";
import { Helmet } from "react-helmet-async";

export function NotFound() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      const v = videoRef.current;
      v.play().catch((err) => {
        console.warn("Video autoplay failed:", err.message);
      });
      
      const handleVideoError = () => {
        const error = v.error;
        console.error("Video Error Details:", {
          code: error?.code,
          message: error?.message,
          src: v.currentSrc || "https://media.tacitonic.com/media/404.mp4?v=7"
        });
      };

      v.addEventListener('error', handleVideoError);
      return () => v.removeEventListener('error', handleVideoError);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>404 - Not Found • Atharva Dhekne</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <main 
        className="relative w-full min-h-screen flex flex-col pt-[120px] pb-12 px-4 md:px-8 transition-colors duration-500 bg-[#000000]"
      >
      <div className="relative w-full flex-1 min-h-[60vh] overflow-hidden border-2 border-white/10 bg-black shadow-2xl flex items-center justify-center">
        
        {/* Background Video Window */}
        <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover z-0 opacity-60"
            src="https://media.tacitonic.com/media/404.mp4?v=7"
          >
            {/* Fallback to dark background if video fails */}
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black opacity-60" />
        </div>

        {/* 404 Text Content */}
        <div className="relative z-10 text-center px-6 flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
            className="space-y-2 pointer-events-auto"
          >
            <h1 className="text-[120px] md:text-[240px] lg:text-[280px] font-display font-bold leading-none tracking-tighter text-white drop-shadow-[0_0_30px_rgba(254,253,249,0.2)]">
              404
            </h1>
            
            <div className="space-y-12 pt-4 flex flex-col items-center">
              <h2 className="text-xl md:text-2xl font-display font-bold uppercase tracking-widest text-white/70">
                Lost Signal
              </h2>
              
              <div className="flex justify-center mix-blend-normal">
                <MagneticButton>
                  <Link 
                    to="/" 
                    className="group flex items-center gap-2 md:gap-3 border-2 border-white/20 hover:border-transparent pl-5 pr-4 py-3 md:pl-8 md:pr-6 md:py-4 hover:bg-white hover:text-black transition-all duration-300 hover-target font-mono text-[10px] md:text-[12px] uppercase tracking-[0.2em] font-bold relative overflow-hidden"
                  >
                    <span className="relative z-10"><TextScramble text="Return Home" /></span> 
                    <ArrowUpRight className="text-[#9af104] transition-opacity relative z-10 w-3.5 h-3.5 md:w-4 md:h-4" />
                  </Link>
                </MagneticButton>
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </main>
    </>
  );
}
